import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../api/supabaseClient";
import ScanResultDetails, { collect } from "../components/ScanResultDetails";
import ScleralResult from "../components/ScleralResult";
import PowerCalculation from "../components/PowerCalculation";
import "./Dashboard.css";

/**
 * One page for both lens types.
 *
 * There used to be a separate /select page asking for a topographer and a
 * lens type before you could reach a dashboard. The topographer answer was
 * written to localStorage and never read by anything -- and the export
 * states its own device, so it is detected rather than asked for. That left
 * one real choice, which lives here as a switch.
 */

const LENSES = {
  scleral: {
    label: "Scleral",
    blurb: "Corneo-scleral export, 20 mm elevation map",
    marker: "_CORNEO-SCLERAL",
    uploadPath: "/scan/upload-scan",
    resultsPath: "/scan/scan-results",
    route: "/dashboard",
  },
  corneal: {
    label: "Corneal",
    blurb: "Corneal topography export",
    marker: null,
    uploadPath: "/corneal/upload-corneal",
    resultsPath: "/corneal/corneal-results",
    route: "/dashboard/corneal",
  },
};

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://ektasia-backend.onrender.com";

export default function Dashboard({ lens = "scleral" }) {
  const navigate = useNavigate();
  const kind = LENSES[lens] ? lens : "scleral";
  const config = LENSES[kind];

  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // for the power card: the lens in the trial lens card, and the diameter and base curve in Lens Specs
  const [trialLens, setTrialLens] = useState(null);
  const [orderedLens, setOrderedLens] = useState(null);
  const inputRef = useRef(null);

  const fetchResults = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_BASE_URL}${config.resultsPath}`, {
        headers: { Authorization: `Bearer ${session.access_token}`, Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`Could not load results (${res.status})`);
      const body = await res.json();
      const rows = (body.data || []).slice().sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setResults(rows);
      setSelectedId((current) =>
        current && rows.some((r) => r.id === current) ? current : rows[0]?.id ?? null
      );
    } catch (err) {
      setError(err.message);
    }
  }, [config.resultsPath]);

  useEffect(() => {
    setResults([]);
    setSelectedId(null);
    setFile(null);
    setError(null);
    fetchResults();
  }, [fetchResults]);

  const chooseFile = (candidate) => {
    if (!candidate) return;
    if (!/\.csv$/i.test(candidate.name)) {
      setError("That is not a .CSV export.");
      return;
    }
    if (config.marker && !candidate.name.includes(config.marker)) {
      setError(`Expected the ${config.marker} export for a scleral fit.`);
      return;
    }
    setError(null);
    setFile(candidate);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Your session expired. Please sign in again.");
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_BASE_URL}${config.uploadPath}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: form,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.detail || `Analysis failed (${res.status})`);
      }
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await fetchResults();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const selected = results.find((r) => r.id === selectedId) || null;

  return (
    <div className="dash">
      <header className="dash-bar">
        <div className="dash-brand">
          <span className="dash-word" aria-label="Ektasia">E<b>K</b>TASIA</span>
        </div>

        <div className="dash-switch" role="tablist" aria-label="Lens type">
          {Object.entries(LENSES).map(([key, value]) => (
            <button
              key={key}
              role="tab"
              aria-selected={key === kind}
              className={key === kind ? "dash-switch-on" : ""}
              onClick={() => navigate(value.route)}
            >
              {value.label}
            </button>
          ))}
        </div>

        <button className="dash-signout" onClick={signOut}>Sign out</button>
      </header>

      <main className="dash-body">
        <aside className="dash-side">
          <section className="dash-card">
            <h2 className="dash-card-title">New analysis</h2>
            <p className="dash-card-sub">{config.blurb}</p>

            <div
              className={`dash-drop${dragging ? " dash-drop-over" : ""}${file ? " dash-drop-set" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                chooseFile(e.dataTransfer.files?.[0]);
              }}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
              role="button"
              tabIndex={0}
            >
              {file ? (
                <>
                  <span className="dash-drop-file">{file.name}</span>
                  <span className="dash-drop-hint">Click to replace</span>
                </>
              ) : (
                <>
                  <span className="dash-drop-icon" aria-hidden="true">↑</span>
                  <span className="dash-drop-file">Drop a .CSV here</span>
                  <span className="dash-drop-hint">or click to browse</span>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".CSV,.csv"
                onChange={(e) => chooseFile(e.target.files?.[0])}
              />
            </div>

            {error && <p className="dash-error" role="alert">{error}</p>}

            <button className="dash-go" onClick={upload} disabled={!file || busy}>
              {busy ? "Analysing…" : "Analyse"}
            </button>
          </section>

          <section className="dash-card dash-card-flush">
            <h2 className="dash-card-title">
              History <span className="dash-count">{results.length}</span>
            </h2>
            {results.length === 0 ? (
              <p className="dash-empty">Nothing yet.</p>
            ) : (
              <ul className="dash-history">
                {results.map((row) => {
                  const c = collect(row.parameters);
                  return (
                    <li key={row.id}>
                      <button
                        className={row.id === selectedId ? "dash-history-on" : ""}
                        onClick={() => setSelectedId(row.id)}
                      >
                        <span className="dash-history-top">
                          <span className="dash-history-eye">{c.eye || "—"}</span>
                          <span className="dash-history-date">
                            {new Date(row.created_at).toLocaleDateString()}
                          </span>
                        </span>
                        <span className="dash-history-code">{row.scan_code}</span>
                        <span className="dash-history-sub">
                          {typeof c.baseCurve === "number" ? `BC ${c.baseCurve.toFixed(2)}` : "—"}
                          {c.rotation?.steep != null && ` · ${c.rotation.steep.toFixed(0)}°`}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {kind === "scleral" && (
            <section className="dash-card">
              {/* a new key per result: one eye's prescriptions are not carried to the next */}
              <PowerCalculation key={selectedId || "none"} trial={selected ? trialLens : null} order={selected ? orderedLens : null} />
            </section>
          )}
        </aside>

        <section className="dash-main">
          {selected && kind === "scleral" ? (
            <ScleralResult
              parameters={selected.parameters}
              createdAt={selected.created_at}
              code={selected.scan_code}
              onTrialLens={setTrialLens}
              onOrder={setOrderedLens}
            />
          ) : selected ? (
            <div className="dash-card dash-result">
              <ScanResultDetails
                parameters={selected.parameters}
                createdAt={selected.created_at}
                code={selected.scan_code}
              />
            </div>
          ) : (
            <div className="dash-card dash-blank">
              <span className="dash-blank-mark" aria-hidden="true">◎</span>
              <h2>No {config.label.toLowerCase()} analyses yet</h2>
              <p>Upload a {config.marker || "topography"} export to generate lens parameters.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
